import React, { forwardRef, useImperativeHandle, useState, useMemo } from "react";
import { TabPanel, Item as TabItem } from "devextreme-react/tab-panel";
import DataGrid from "devextreme-react/data-grid";
// Reused OM Sales Order Management result grids (self-contained, take a `data` store prop). Their backend
// endpoints key off the selected item's rowid, so they work unchanged for the IM Item Master tab.
import ItemOrdersTabResult from "../../../om/transactions/salesorders/salesOrderManagement/itemMaster/ItemOrdersTabResult";
import OpenRequisitionsTabResult from "../../../om/transactions/salesorders/salesOrderManagement/itemMaster/OpenRequisitionsTabResult";
import OpenPosTabResult from "../../../om/transactions/salesorders/salesOrderManagement/itemMaster/OpenPosTabResult";
import OpenPosReceiptTabResult from "../../../om/transactions/salesorders/salesOrderManagement/itemMaster/OpenPosReceiptTabResult";
import ItemLocationTabResult from "../../../om/transactions/salesorders/salesOrderManagement/itemMaster/ItemLocationTabResult";
import LotTab from "../../../im/codes/itemDataEntry/tabs/LotTab";
import { GetOrderDetailGridData } from "../../../om/services/SalesOrderService";
import {
  GetItemTransactionsGridData,
  GetTransactionHeaderGridData,
  GetVendorCrossReferenceGridData,
} from "../services/ItemManagementService";
import { ITEM_TRANSACTION_COLUMNS, VENDOR_CR_COLUMNS, TRANSACTION_HEADER_COLUMNS } from "./subTabColumns";
import apiRoutes from "../../../assets/apiRoutes.json";

// Reused OM endpoint routes (item-keyed — the item's rowid is passed as headerId → RItem).
const R = apiRoutes.OM.SaleOrder;

// Shared read-only sub-grid for the IM-specific tabs (same look/height as the transactions bottom sub-grids).
const SubGrid = ({ data, columns }) => (
  <div style={{ height: 305 }}>
    <DataGrid
      dataSource={data}
      columns={columns}
      remoteOperations={true}
      showBorders={true}
      columnAutoWidth={true}
      height="100%"
      sorting={{ mode: "multiple" }}
      filterRow={{ visible: true }}
      headerFilter={{ visible: true }}
      columnChooser={{ enabled: true }}
      export={{ enabled: true }}
      paging={{ pageSize: 20 }}
      pager={{ visible: true, showPageSizeSelector: true, allowedPageSizes: [20, 50, 100], showInfo: true }}
    />
  </div>
);

const noSelection = (
  <div style={{ padding: 16, color: "#888" }}>Select an item above to load this tab.</div>
);

// Bottom sub-tabs for the Item Master top tab (Delphi IMD0046). Only tabs that key off the selected item
// re-fetch on selection (imperative updateData → selectedRow). Item Transactions / Vendor Cross Reference /
// Open & Historical Transactions are IM-specific and land in Stage B.
const ItemMasterSubTabs = forwardRef(({ accessToken }, ref) => {
  const [selectedRow, setSelectedRow] = useState(null);

  useImperativeHandle(ref, () => ({
    updateData(rowData) { setSelectedRow(rowData); },
  }));

  const itemId = selectedRow && selectedRow.rowid ? selectedRow.rowid : null;

  const omStore = useMemo(() => (itemId ? GetOrderDetailGridData(accessToken, R.GetItemOrder, itemId) : null), [itemId, accessToken]);
  const openReqStore = useMemo(() => (itemId ? GetOrderDetailGridData(accessToken, R.GetOpenRequisitions, itemId) : null), [itemId, accessToken]);
  const posStore = useMemo(() => (itemId ? GetOrderDetailGridData(accessToken, R.GetPos, itemId) : null), [itemId, accessToken]);
  const poReceiptStore = useMemo(() => (itemId ? GetOrderDetailGridData(accessToken, R.GetPosReceipt, itemId) : null), [itemId, accessToken]);
  const itemLocStore = useMemo(() => (itemId ? GetOrderDetailGridData(accessToken, R.GetItemLocations, itemId) : null), [itemId, accessToken]);
  // IM-specific tabs keyed off the selected item.
  const itemTransStore = useMemo(() => (itemId ? GetItemTransactionsGridData(accessToken, { itemId }) : null), [itemId, accessToken]);
  const vendorCrStore = useMemo(() => (itemId ? GetVendorCrossReferenceGridData(accessToken, { itemId }) : null), [itemId, accessToken]);
  // Open Transactions is always-on (show all). Historical Transactions is item-filtered (Delphi
  // qeItemMasterTopAfterScroll) — only historical headers with a detail line for the selected item.
  const openTransStore = useMemo(() => GetTransactionHeaderGridData(accessToken, false), [accessToken]);
  const histTransStore = useMemo(() => (itemId ? GetTransactionHeaderGridData(accessToken, true, null, { itemId }) : null), [itemId, accessToken]);

  return (
    <TabPanel deferRendering={true} animationEnabled={false}>
      <TabItem title="Item Transactions">{itemId ? <SubGrid data={itemTransStore} columns={ITEM_TRANSACTION_COLUMNS} /> : noSelection}</TabItem>
      <TabItem title="Lot"><LotTab key={itemId || "no-selection"} rowId={itemId} includeAllStatuses={true} gridHeight={305} /></TabItem>
      <TabItem title="Vendor Cross Reference">{itemId ? <SubGrid data={vendorCrStore} columns={VENDOR_CR_COLUMNS} /> : noSelection}</TabItem>
      <TabItem title="Open Requisitions"><OpenRequisitionsTabResult data={openReqStore} /></TabItem>
      <TabItem title="Open PO's"><OpenPosTabResult data={posStore} /></TabItem>
      <TabItem title="PO Receipts"><OpenPosReceiptTabResult data={poReceiptStore} /></TabItem>
      <TabItem title="OM Transactions"><ItemOrdersTabResult data={omStore} /></TabItem>
      <TabItem title="Item/Locations"><ItemLocationTabResult data={itemLocStore} /></TabItem>
      <TabItem title="Open Transactions"><SubGrid data={openTransStore} columns={TRANSACTION_HEADER_COLUMNS} /></TabItem>
      <TabItem title="Historical Transactions">{itemId ? <SubGrid data={histTransStore} columns={TRANSACTION_HEADER_COLUMNS} /> : noSelection}</TabItem>
    </TabPanel>
  );
});

export default ItemMasterSubTabs;
